import React from 'react'

import { createColumnHelper } from '@tanstack/react-table'
import { useTranslation } from 'react-i18next'

import { Box, Flex, Text, Tooltip } from '@enechain/ecloud-designsystem'
import { FaCircleQuestionRegular } from '@enechain/ecloud-icons'
import {
  StatusBadge,
  getTextAlignLightTableStyle,
} from '@enechain/eposition-ui'
import { stringUtils } from '@enechain/eposition-utils'

import { AvailableDealBaseTable } from '~/pages/Simulation/components/SimulationAndResultArea/AvailableDealSimulationSection/ResultSection/AvailableDealBaseTable'
import { ResultAvailableDealSimulationForOption1Models } from '~/pages/Simulation/models/SimulationResultModel/ResultAvailableDealSimulationDetailForOption1Models'
import { ResultSimulationForOption1Model } from '~/pages/Simulation/models/SimulationResultModel/ResultSimulationForOption1Model'

type Props = {
  resultDetail: ResultAvailableDealSimulationForOption1Models | undefined
}

const RELATION_COLUMN_WIDTH = 72
const DEAL_STATUS_COLUMN_WIDTH = 60

export const ResultAvailableDealSimulationForOption1Table: React.FC<Props> = ({
  resultDetail,
}) => {
  const { t } = useTranslation(['page', 'common', 'domain'])
  const columnHelper = createColumnHelper<ResultSimulationForOption1Model>()

  const columns = [
    columnHelper.accessor('orderRelationString', {
      header: t('page:simulation.order_relation_number'),
      cell: (info) => <Box>{info.getValue()}</Box>,
      enableSorting: false,
      size: RELATION_COLUMN_WIDTH,
      meta: {
        headerStyle: {
          width: stringUtils.convertPxFromNumber(RELATION_COLUMN_WIDTH),
        },
        cellStyle: {
          width: stringUtils.convertPxFromNumber(RELATION_COLUMN_WIDTH),
        },
      },
    }),
    columnHelper.accessor('allowDealStatus', {
      header: t('page:simulation.deal_availability'),
      cell: (info) => {
        const statusValue = info.getValue()
        return <StatusBadge value={statusValue} />
      },
      enableSorting: false,
      meta: {
        headerStyle: {
          width: stringUtils.convertPxFromNumber(DEAL_STATUS_COLUMN_WIDTH),
        },
        cellStyle: {
          width: stringUtils.convertPxFromNumber(DEAL_STATUS_COLUMN_WIDTH),
        },
      },
    }),
    columnHelper.accessor('requiredMarginAmount', {
      header: t('page:simulation.required_margin'),
      cell: (info) => <Box>{info.getValue()}</Box>,
      enableSorting: false,
      meta: getTextAlignLightTableStyle(),
    }),
    columnHelper.accessor('surplusAmount', {
      header: () => (
        <Flex justifyContent="flex-end" alignItems="center" gap="1">
          <Text>{t('page:simulation.surplus_or_deficit')}</Text>
          <Tooltip
            label={t('page:simulation.surplus_or_deficit_description')}
            showArrow
            placement="top"
          >
            <FaCircleQuestionRegular boxSize="20px" />
          </Tooltip>
        </Flex>
      ),
      cell: (info) => <Box>{info.getValue()}</Box>,
      enableSorting: false,
      meta: getTextAlignLightTableStyle(),
    }),
  ]

  if (resultDetail === undefined) {
    return null
  }

  return (
    <AvailableDealBaseTable data={resultDetail.details} columns={columns} />
  )
}
